import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Collapse,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Tooltip,
  Dialog,
  DialogContent,
} from '@mui/material'
import ExpandMore from '@mui/icons-material/ExpandMore'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ImageIcon from '@mui/icons-material/Image'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import DeleteIcon from '@mui/icons-material/Delete'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { postMPDataWithParams } from 'src/mentor/services'
import { DocumentEditorDialog } from 'src/consumer/components/Helpers/DocumentEditor'
import { getDataWithParams } from 'src/consumer/services/profileService'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import EmrImagesUploadDialog from 'src/clinic/components/ClinicManageBookingsPage/emrImagesUpload/EmrImagesUploadDialog'
import { getEmrApiData, getTherapyReportApis } from 'src/shared/utils/calendarReportApiHelper'
import { PORTAL_TYPES } from 'src/shared/config/calendarReportApiConfig'

const StyledCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(67, 56, 202, 0.1);
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
`

const ThumbnailContainer = styled(Box)`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
`

const Thumbnail = styled(Box)`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid rgba(67, 56, 202, 0.2);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: rgba(67, 56, 202, 0.5);
    transform: scale(1.05);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ImageItem = styled(Box)`
  margin-bottom: 16px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(67, 56, 202, 0.1);
  position: relative;
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }
`

const DeleteOverlay = styled(Box)`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
`

const ThumbnailWrapper = styled(Box)`
  position: relative;
  display: inline-block;
`

const ThumbnailDeleteButton = styled(IconButton)`
  position: absolute;
  top: -6px;
  right: -6px;
  background: #ef4444;
  color: white;
  padding: 2px;
  z-index: 10;
  
  &:hover {
    background: #dc2626;
  }
  
  & .MuiSvgIcon-root {
    font-size: 14px;
  }
`

const ThumbnailEditButton = styled(IconButton)`
  position: absolute;
  top: -6px;
  left: -6px;
  background: #4338ca;
  color: white;
  padding: 2px;
  z-index: 10;
  
  &:hover {
    background: #3730a3;
  }
  
  & .MuiSvgIcon-root {
    font-size: 14px;
  }
`

const EditOverlay = styled(Box)`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 10;
`

// Styled confirmation dialog components
const ConfirmDialogContent = styled(Box)`
  padding: 24px;
  text-align: center;
  max-width: 320px;
`

const ConfirmDialogIconWrapper = styled(Box)`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.25);
`

const ConfirmDialogTitle = styled(Typography)`
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`

const ConfirmDialogMessage = styled(Typography)`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 24px;
  line-height: 1.5;
`

const ConfirmDialogActions = styled(Box)`
  display: flex;
  gap: 12px;
  justify-content: center;
`

const CancelButton = styled(Button)`
  padding: 10px 24px;
  border-radius: 10px;
  text-transform: none;
  font-weight: 600;
  font-size: 14px;
  color: #6b7280;
  background: #f3f4f6;
  border: none;
  
  &:hover {
    background: #e5e7eb;
  }
`

const DeleteButton = styled(Button)`
  padding: 10px 24px;
  border-radius: 10px;
  text-transform: none;
  font-weight: 600;
  font-size: 14px;
  color: white;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.35);
  
  &:hover {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    box-shadow: 0 6px 16px rgba(239, 68, 68, 0.45);
  }
  
  &:disabled {
    background: #fca5a5;
    box-shadow: none;
  }
`

export default function DocumentsUploadSection({ eventId, bookedEventInfo, therapyReportData, mentorProfileData, onImagesUpdate }) {
  const [expanded, setExpanded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [images, setImages] = useState([])
  const [signedUrls, setSignedUrls] = useState([])
  const [error, setError] = useState(null)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [deletingIndex, setDeletingIndex] = useState(null)
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, index: null, url: null })
  const [documentEditorState, setDocumentEditorState] = useState({ open: false, index: null, url: null })

  // Helper function to convert image ID to media URL
  const getMediaUrl = (imageId) => {
    if (!imageId) return ''
    // If it's already a full URL, return as is
    if (imageId.startsWith('http://') || imageId.startsWith('https://')) {
      return imageId
    }
    // Otherwise, convert image ID to media URL
    return `https://media.aumhum.com/images/prod/${imageId}`
  }

  // Determine if we're in mentor context
  // If mentorProfileData exists, we're in mentor context (even if URL is /l/*)
  // Also check URL path for /l/* routes (video call page) as fallback
  const isMentorContext = useMemo(() => {
    // Primary check: mentorProfileData is the source of truth
    if (mentorProfileData) return true
    
    // Fallback: Check if we're on /l/* route (video call page)
    // This is typically accessed by mentors, so treat as mentor context
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/l/')) {
      return true
    }
    
    return false
  }, [mentorProfileData])

  // Get API signatures using helper
  // If mentorProfileData exists or we're on /l/* route, explicitly set portal type to MENTOR
  // This ensures we get the correct APIs even if portal detection fails (e.g., /l/* routes)
  const emrApiData = useMemo(() => {
    // Always use MENTOR portal type if we have mentorProfileData
    // This is critical for /l/* routes where portal detection fails
    if (isMentorContext) {
      // Use direct call to getTherapyReportApis to bypass event type mapping
      const directApis = getTherapyReportApis({ portalType: PORTAL_TYPES.MENTOR })
      
      // Return the APIs if we have them
      if (directApis?.get && directApis?.save && directApis?.upload) {
        return {
          getApiSignature: directApis.get,
          saveApiSignature: directApis.save,
          uploadImagesSignature: directApis.upload,
          dialogTitle: 'Upload Therapy Report Images',
        }
      }
      
      // Hardcoded fallback for mentor portal if helper functions fail
      // This ensures we always have APIs available in mentor context
      return {
        getApiSignature: '/api/secure/mentor/emr-reports/therapy-report/images/get-by-eventId',
        saveApiSignature: '/api/secure/mentor/emr-reports/therapy-report/images/save',
        uploadImagesSignature: '/api/secure/mentor/emr-reports/file/upload',
        dialogTitle: 'Upload Therapy Report Images',
      }
    }
    
    // Fallback to getEmrApiData for non-mentor contexts
    return getEmrApiData('therapy-session', { 
      portalType: isMentorContext ? PORTAL_TYPES.MENTOR : undefined 
    })
  }, [isMentorContext, mentorProfileData])
  
  // Warn if API signatures are missing (only log errors, not debug info)
  useEffect(() => {
    // Only log error if we truly don't have any API signatures
    // This can happen if portal detection fails and mentorProfileData is not available
    if (!emrApiData?.getApiSignature || !emrApiData?.saveApiSignature || !emrApiData?.uploadImagesSignature) {
      // Only log if we're in mentor context (where we should have APIs)
      // or if we don't have eventId (which means we can't fetch anyway)
      if (isMentorContext || !eventId) {
        console.error('DocumentsUploadSection: Missing API signatures!', {
          hasGet: !!emrApiData?.getApiSignature,
          hasSave: !!emrApiData?.saveApiSignature,
          hasUpload: !!emrApiData?.uploadImagesSignature,
          isMentorContext,
          portalType: PORTAL_TYPES.MENTOR,
          hasEventId: !!eventId,
        })
      }
    }
  }, [emrApiData, isMentorContext, eventId])

  const fetchImages = useCallback(async () => {
    // First, check if images are already in therapy report data
    // Only use therapyReportData if BOTH images AND signed URLs are present
    // Otherwise, fetch from API to get fresh signed URLs
    const hasImagesInReport = therapyReportData?.uploadedImages && therapyReportData.uploadedImages.length > 0
    const hasSignedUrlsInReport = therapyReportData?.uploadedSignedImages && therapyReportData.uploadedSignedImages.length > 0
    
    // Only use therapyReportData if we have both images and signed URLs
    // This ensures we always have signed URLs to display
    if (hasImagesInReport && hasSignedUrlsInReport && 
        therapyReportData.uploadedImages.length === therapyReportData.uploadedSignedImages.length) {
      setImages(therapyReportData.uploadedImages)
      setSignedUrls(therapyReportData.uploadedSignedImages)
      setLoading(false)
      setError(null)
      return
    }

    // If not in report data, fetch from API
    if (!eventId) {
      return
    }

    if (!emrApiData?.getApiSignature) {
      // Only log error in development or if we truly need the API (have eventId)
      if (process.env.NODE_ENV === 'development' || eventId) {
        console.error('DocumentsUploadSection: No API signature available', { 
          emrApiData,
          hasGetSignature: !!emrApiData?.getApiSignature,
          hasSaveSignature: !!emrApiData?.saveApiSignature,
          hasUploadSignature: !!emrApiData?.uploadImagesSignature,
          portalType: PORTAL_TYPES.MENTOR,
          isMentorContext,
          eventId,
        })
      }
      // Don't return early - we might still have images from report data
      // But set an error so user knows something is wrong
      if (eventId) {
        setError('Unable to fetch images: API configuration not available. Please refresh the page.')
      }
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const resp = await getDataWithParams(emrApiData.getApiSignature, {
        eventId,
      })

      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        const data = getDataMapFromCommonResponse(resp)
        
        // Try multiple possible field names for image IDs and URLs
        const imageIds = data?.reportImages ?? data?.uploadedImages ?? data?.fileIds ?? []
        const urls = data?.signedURLs ?? data?.signedUrls ?? data?.uploadedSignedImages ?? []
        
        // If we have image IDs but no signed URLs, convert image IDs to media URLs
        let finalUrls = urls
        if (urls.length === 0 && imageIds.length > 0) {
          finalUrls = imageIds.map(id => getMediaUrl(id))
        }
        
        // Also check therapyReportData for uploadedSignedImages as fallback
        if (finalUrls.length === 0 && therapyReportData?.uploadedSignedImages?.length > 0) {
          finalUrls = therapyReportData.uploadedSignedImages.map(img => getMediaUrl(img))
        }
        
        // Also check if we have uploadedImages in therapyReportData but no URLs
        if (finalUrls.length === 0 && therapyReportData?.uploadedImages?.length > 0) {
          finalUrls = therapyReportData.uploadedImages.map(id => getMediaUrl(id))
        }
        
        // Set images and URLs
        const finalImageIds = imageIds.length > 0 ? imageIds : (therapyReportData?.uploadedImages || [])
        setImages(finalImageIds)
        setSignedUrls(finalUrls)
      } else {
        const errorMsg = resp?.data?.message || 'Failed to load images'
        console.error('DocumentsUploadSection: API error', {
          status: resp?.status,
          message: errorMsg,
          response: resp?.data,
        })
        setError(errorMsg)
      }
    } catch (err) {
      console.error('DocumentsUploadSection: Exception fetching images', err)
      setError(err?.message || 'Failed to load images')
    } finally {
      setLoading(false)
    }
  }, [eventId, emrApiData?.getApiSignature, therapyReportData?.uploadedImages, therapyReportData?.uploadedSignedImages])

  // Sync images from therapyReportData whenever it changes
  // Convert image IDs to media URLs if signed URLs are not available
  useEffect(() => {
    if (therapyReportData) {
      const hasImages = therapyReportData.uploadedImages && therapyReportData.uploadedImages.length > 0
      const hasSignedUrls = therapyReportData.uploadedSignedImages && therapyReportData.uploadedSignedImages.length > 0
      
      // If we have uploadedSignedImages, use them (convert to URLs if needed)
      if (hasSignedUrls) {
        const urls = therapyReportData.uploadedSignedImages.map(img => getMediaUrl(img))
        setImages(therapyReportData.uploadedImages || [])
        setSignedUrls(urls)
      } 
      // If we have uploadedImages but no signed URLs, convert IDs to media URLs
      else if (hasImages) {
        const urls = therapyReportData.uploadedImages.map(id => getMediaUrl(id))
        setImages(therapyReportData.uploadedImages)
        setSignedUrls(urls)
      }
    }
  }, [therapyReportData?.uploadedImages, therapyReportData?.uploadedSignedImages])

  useEffect(() => {
    fetchImages()
  }, [fetchImages])

  const handleToggleExpand = () => {
    setExpanded(!expanded)
  }

  const openDeleteConfirmDialog = (index) => {
    if (deletingIndex !== null) return // Prevent multiple deletions at once
    setDeleteConfirmDialog({ open: true, index, url: signedUrls[index] })
  }

  const closeDeleteConfirmDialog = () => {
    setDeleteConfirmDialog({ open: false, index: null, url: null })
  }

  const openDocumentEditor = (index) => {
    setDocumentEditorState({ open: true, index, url: signedUrls[index] })
  }

  const closeDocumentEditor = () => {
    setDocumentEditorState({ open: false, index: null, url: null })
  }

  const handleDocumentEditorSave = async ({ blob }) => {
    // The edited image will be uploaded as a new image
    // In a full implementation, you would upload the blob to server and update the images array
    toast.success('Document enhanced! You can download the result.')
    closeDocumentEditor()
    // Note: To fully integrate, you'd upload the blob and replace the image:
    // 1. Upload blob using emrApiData.uploadImagesSignature
    // 2. Update images array with new image ID
    // 3. Call onImagesUpdate
  }

  const handleDeleteImage = async () => {
    const indexToDelete = deleteConfirmDialog.index
    if (indexToDelete === null) return
    
    closeDeleteConfirmDialog()
    setDeletingIndex(indexToDelete)
    
    try {
      // Remove the image from arrays
      const updatedImages = images.filter((_, index) => index !== indexToDelete)
      const updatedSignedUrls = signedUrls.filter((_, index) => index !== indexToDelete)
      
      // Save the updated images array to the server
      if (emrApiData?.saveApiSignature && eventId) {
        const response = await postMPDataWithParams(
          emrApiData.saveApiSignature,
          { reportImages: updatedImages },
          { eventId }
        )
        
        if (response?.status === 200) {
          // Update local state
          setImages(updatedImages)
          setSignedUrls(updatedSignedUrls)
          toast.success('Document deleted successfully')
          
          // Notify parent component if callback provided
          if (onImagesUpdate) {
            onImagesUpdate(updatedImages)
          }
        } else {
          throw new Error(response?.data?.message || 'Failed to delete document')
        }
      } else {
        // If no API, just update local state
        setImages(updatedImages)
        setSignedUrls(updatedSignedUrls)
        toast.success('Document removed')
        
        if (onImagesUpdate) {
          onImagesUpdate(updatedImages)
        }
      }
    } catch (err) {
      console.error('DocumentsUploadSection: Error deleting image', err)
      toast.error(err?.message || 'Failed to delete document')
    } finally {
      setDeletingIndex(null)
    }
  }

  const imageCount = images?.length || 0

  return (
    <>
      <StyledCard>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
            onClick={handleToggleExpand}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
                  color: '#4338ca',
                }}
              >
                <AttachFileIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: '15px',
                    fontWeight: 700,
                    color: '#1f2937',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  }}
                >
                  Documents Upload
                </Typography>
                {loading ? (
                  <Typography sx={{ fontSize: '12px', color: '#6b7280', mt: 0.5 }}>
                    Loading...
                  </Typography>
                ) : (imageCount > 0 || signedUrls.length > 0) ? (
                  <Typography sx={{ fontSize: '12px', color: '#6b7280', mt: 0.5 }}>
                    {imageCount || signedUrls.length} {(imageCount || signedUrls.length) === 1 ? 'document' : 'documents'} uploaded
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: '12px', color: '#6b7280', mt: 0.5 }}>
                    No documents uploaded
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {(imageCount > 0 || signedUrls.length > 0) && (
                <Chip
                  label={imageCount || signedUrls.length}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(67, 56, 202, 0.1)',
                    color: '#4338ca',
                    fontWeight: 600,
                    fontSize: '11px',
                  }}
                />
              )}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  handleToggleExpand()
                }}
                sx={{
                  color: '#4338ca',
                  '&:hover': {
                    backgroundColor: 'rgba(67, 56, 202, 0.1)',
                  },
                }}
              >
                {expanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
          </Box>

          {/* Show thumbnails even when collapsed */}
          {!expanded && signedUrls.length > 0 && (
            <Box sx={{ mt: 2, pt: 2 }}>
              <ThumbnailContainer>
                {signedUrls.slice(0, 6).map((url, index) => (
                  <ThumbnailWrapper key={`collapsed-thumb-${index}-${url?.substring(0, 20)}`}>
                    <Tooltip title="Edit & Enhance">
                      <ThumbnailEditButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          openDocumentEditor(index)
                        }}
                      >
                        <EditIcon />
                      </ThumbnailEditButton>
                    </Tooltip>
                    <Tooltip title="Delete document">
                      <ThumbnailDeleteButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          openDeleteConfirmDialog(index)
                        }}
                        disabled={deletingIndex === index}
                      >
                        {deletingIndex === index ? (
                          <CircularProgress size={10} sx={{ color: 'white' }} />
                        ) : (
                          <DeleteIcon />
                        )}
                      </ThumbnailDeleteButton>
                    </Tooltip>
                    <Thumbnail onClick={() => setExpanded(true)}>
                      <img 
                        src={url} 
                        alt={`Document ${index + 1}`}
                        onError={(e) => {
                          console.error('DocumentsUploadSection: Thumbnail load error', {
                            url,
                            index,
                          })
                          e.target.style.display = 'none'
                        }}
                      />
                    </Thumbnail>
                  </ThumbnailWrapper>
                ))}
                {signedUrls.length > 6 && (
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(67, 56, 202, 0.1)',
                      color: '#4338ca',
                      fontWeight: 600,
                      fontSize: '12px',
                      cursor: 'pointer',
                    }}
                    onClick={() => setExpanded(true)}
                  >
                    +{signedUrls.length - 6}
                  </Box>
                )}
              </ThumbnailContainer>
            </Box>
          )}

          <Collapse in={expanded}>
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(67, 56, 202, 0.1)' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} sx={{ color: '#4338ca' }} />
                </Box>
              ) : error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              ) : (imageCount === 0 && signedUrls.length === 0) ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 4,
                    color: '#6b7280',
                  }}
                >
                  <ImageIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
                  <Typography sx={{ fontSize: '14px', mb: 2 }}>
                    No documents uploaded yet
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AttachFileIcon />}
                    onClick={() => setUploadDialogOpen(true)}
                    sx={{
                      background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Upload Documents
                  </Button>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                      Uploaded Documents ({imageCount || signedUrls.length})
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AttachFileIcon />}
                      onClick={(e) => {
                        e.stopPropagation()
                        setUploadDialogOpen(true)
                      }}
                      sx={{
                        borderColor: '#4338ca',
                        color: '#4338ca',
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          borderColor: '#3730a3',
                          backgroundColor: 'rgba(67, 56, 202, 0.05)',
                        },
                      }}
                    >
                      Add More
                    </Button>
                  </Box>

                  {/* Thumbnails - Show even when collapsed if there are images */}
                  {signedUrls.length > 0 && (
                    <ThumbnailContainer>
                      {signedUrls.map((url, index) => (
                        <ThumbnailWrapper key={`thumb-${index}-${url?.substring(0, 20)}`}>
                          <Tooltip title="Edit & Enhance">
                            <ThumbnailEditButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation()
                                openDocumentEditor(index)
                              }}
                            >
                              <EditIcon />
                            </ThumbnailEditButton>
                          </Tooltip>
                          <Tooltip title="Delete document">
                            <ThumbnailDeleteButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation()
                                openDeleteConfirmDialog(index)
                              }}
                              disabled={deletingIndex === index}
                            >
                              {deletingIndex === index ? (
                                <CircularProgress size={10} sx={{ color: 'white' }} />
                              ) : (
                                <DeleteIcon />
                              )}
                            </ThumbnailDeleteButton>
                          </Tooltip>
                          <Thumbnail onClick={() => openDocumentEditor(index)}>
                            <img 
                              src={url} 
                              alt={`Document ${index + 1}`}
                              onError={(e) => {
                                console.error('DocumentsUploadSection: Image load error', {
                                  url,
                                  index,
                                  error: e,
                                })
                                e.target.style.display = 'none'
                              }}
                            />
                          </Thumbnail>
                        </ThumbnailWrapper>
                      ))}
                    </ThumbnailContainer>
                  )}

                  {/* Full Images */}
                  <Box sx={{ mt: 3 }}>
                    {signedUrls.length > 0 ? (
                      signedUrls.map((url, index) => (
                        <ImageItem key={`full-${index}-${url?.substring(0, 20)}`}>
                          <EditOverlay>
                            <Tooltip title="Edit & Enhance Document">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  openDocumentEditor(index)
                                }}
                                sx={{
                                  backgroundColor: '#4338ca',
                                  color: 'white',
                                  '&:hover': {
                                    backgroundColor: '#3730a3',
                                  },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </EditOverlay>
                          <DeleteOverlay>
                            <Tooltip title="Delete document">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  openDeleteConfirmDialog(index)
                                }}
                                disabled={deletingIndex === index}
                                sx={{
                                  backgroundColor: '#ef4444',
                                  color: 'white',
                                  '&:hover': {
                                    backgroundColor: '#dc2626',
                                  },
                                  '&:disabled': {
                                    backgroundColor: '#fca5a5',
                                    color: 'white',
                                  },
                                }}
                              >
                                {deletingIndex === index ? (
                                  <CircularProgress size={16} sx={{ color: 'white' }} />
                                ) : (
                                  <DeleteIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </DeleteOverlay>
                          <img 
                            src={url} 
                            alt={`Document ${index + 1}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => openDocumentEditor(index)}
                            onError={(e) => {
                              console.error('DocumentsUploadSection: Full image load error', {
                                url,
                                index,
                                error: e,
                              })
                              e.target.style.display = 'none'
                            }}
                          />
                        </ImageItem>
                      ))
                    ) : null}
                  </Box>
                </>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </StyledCard>

      {/* Upload Dialog */}
      {uploadDialogOpen && (
        <EmrImagesUploadDialog
          open={uploadDialogOpen}
          bookedEventInfo={bookedEventInfo || { id: eventId, eventType: 'therapy-session' }}
          closePopover={() => {
            setUploadDialogOpen(false)
          }}
          onSuccess={(savedImageIds) => {
            // Immediately refresh images when save is successful (force refresh to bypass cache)
            console.log('[DocumentsUploadSection] onSuccess received! Images saved:', savedImageIds)
            fetchImages(true) // forceRefresh = true to bypass early return
          }}
          showGallery={false}
          fileUploadApiSignature={emrApiData?.uploadImagesSignature || ''}
          emrImagesGetApiSignature={emrApiData?.getApiSignature || ''}
          emrImagesSaveApiSignature={emrApiData?.saveApiSignature || ''}
          dialogTitle={emrApiData?.dialogTitle || "Upload Therapy Report Documents"}
          initialImages={images}
          initialSignedUrls={signedUrls}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={closeDeleteConfirmDialog}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            maxWidth: '360px',
            margin: '16px',
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* Close button */}
          <IconButton
            onClick={closeDeleteConfirmDialog}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: '#9ca3af',
              '&:hover': {
                color: '#6b7280',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <ConfirmDialogContent>
            <ConfirmDialogIconWrapper>
              <WarningAmberIcon sx={{ fontSize: 32, color: '#f59e0b' }} />
            </ConfirmDialogIconWrapper>
            
            <ConfirmDialogTitle>
              Delete Document?
            </ConfirmDialogTitle>
            
            <ConfirmDialogMessage>
              This action cannot be undone. The document will be permanently removed from this therapy report.
            </ConfirmDialogMessage>

            {/* Preview thumbnail */}
            {deleteConfirmDialog.url && (
              <Box
                sx={{
                  width: '80px',
                  height: '80px',
                  margin: '0 auto 20px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '2px solid #e5e7eb',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
              >
                <img
                  src={deleteConfirmDialog.url}
                  alt="Document to delete"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            )}

            <ConfirmDialogActions>
              <CancelButton onClick={closeDeleteConfirmDialog}>
                Cancel
              </CancelButton>
              <DeleteButton
                onClick={handleDeleteImage}
                startIcon={<DeleteIcon />}
              >
                Delete
              </DeleteButton>
            </ConfirmDialogActions>
          </ConfirmDialogContent>
        </DialogContent>
      </Dialog>

      {/* Document Editor Dialog */}
      <DocumentEditorDialog
        open={documentEditorState.open}
        onClose={closeDocumentEditor}
        imageUrl={documentEditorState.url}
        onSave={handleDocumentEditorSave}
        initialTab={0}
      />
    </>
  )
}

